
import {
  CartesianGrid,
  LabelList,
  Line,
  LineChart,
  XAxis,
  YAxis,
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Bar,
  BarChart,
} from "recharts"
import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card"
import { type ChartConfig, ChartContainer, ChartTooltip } from "../../../components/ui/chart"
import { useEffect, useState, useCallback } from "react"
import { format, parseISO } from "date-fns"
import { ptBR } from "date-fns/locale"
import { Button } from "../../../components/ui/button"
import { RefreshCw, Users, UserCheck, UserX } from "lucide-react"
import { useRemoteClientPost } from "@/configs/http-client"
import { useLocalStorage } from "@/remote/local-storage"
import { useSearchParams } from "react-router-dom"

const chartConfig = {
  vendas: {
    label: "Vendas",
    color: "#00A000",
  },
  total: {
    label: "Total (R$)",
    color: "#EE7101",
  },
} satisfies ChartConfig

const statusColors = {
  ativo: "#22c55e",
  inadimplente: "#ef4444",
  adimplente: "#22c55e",
  bloqueado: "#6b7280",
  cancelado: "#8b5cf6",
}

interface Props {
  dataInicial: string
  dataFinal: string
}

interface VendaData {
  id: number
  total: number
  inclusao: string
  metodoPagamento: string
  origem: string
  aluno?: {
    nome: string
  }
  cliente?: {
    nome: string
  }
}

interface StudentData {
  id: number
  status: number
  nome: string
  inclusao: string
}

interface ChartData {
  month: string
  vendas: number
  total: number
  periodo: string
}

interface StatusChartData {
  name: string
  value: number
  color: string
}

interface RevenueData {
  label: string
  valor: number
}

export function Overview({ dataFinal, dataInicial }: Props) {
  const { httpClientPost: filterBase } = useRemoteClientPost("base")
  const { httpClientPost: filterFinance } = useRemoteClientPost("finance")
  const { getUnitId } = useLocalStorage()
  const [params] = useSearchParams()

  // States
  const [salesData, setSalesData] = useState<ChartData[]>([])
  const [activeStudentsData, setActiveStudentsData] = useState<StatusChartData[]>([])
  const [paymentStatusData, setPaymentStatusData] = useState<StatusChartData[]>([])
  const [blockCancelData, setBlockCancelData] = useState<StatusChartData[]>([])
  const [prospectsData, setProspectsData] = useState<any[]>([])
  const [prospectsCount, setProspectsCount] = useState(0)
  const [revenueData, setRevenueData] = useState<RevenueData[]>([])
  const [revenueByUnitData, setRevenueByUnitData] = useState<RevenueData[]>([])

  const [loading, setLoading] = useState(true)
  const [studentsLoading, setStudentsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchVendasData = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      await new Promise((resolve) => setTimeout(resolve, 1000))
      const response = await filterBase(`/relatorios/financeiro/vendas`, {
        dataInicialVenda: dataInicial,
        dataFinalVenda: dataFinal,
      })
      if (response.status < 200 || response.status >= 300) {
        throw new Error(`Erro na API: ${response.status}`)
      }
      const data = response?.data?.data?.content ?? {}
      const vendasPorMes = processarVendasPorMes(data)
      setSalesData(vendasPorMes)
    } catch (err) {
      console.error("Erro ao buscar dados de vendas:", err)
      setError(err instanceof Error ? err.message : "Erro desconhecido")
    } finally {
      setLoading(false)
    }
  }, [dataInicial, dataFinal])

  const fetchStudentsData = useCallback(async () => {
    try {
      setStudentsLoading(true)
      const unidadeId = params.get("id") ?? getUnitId()
      const response = await filterBase("/alunos/filtro?page=0&size=1000", {
        unidadeId: unidadeId,
        status: [1, 2, 3, 4, 5],
      })
      const studentsData = response?.data?.data?.content ?? []
      processStudentsData(studentsData)
    } catch (err) {
      console.error("Erro ao buscar dados de alunos:", err)
    } finally {
      setStudentsLoading(false)
    }
  }, [params, getUnitId])

  const fetchRevenueData = useCallback(async () => {
    try {
      const body = {
        integracaoStatus: "PENDENTE",
        dataVencimentoInicial: dataInicial,
        dataVencimentoFinal: dataFinal,
      }
      const response = await filterFinance("/graficos/faturamento", body)
      if (response?.data?.data) {
        setRevenueData(response.data.data)
      }
    } catch (err) {
      console.error("Erro ao buscar faturamento:", err)
    }
  }, [dataInicial, dataFinal])

  const fetchRevenueByUnitData = useCallback(async () => {
    try {
      const body = {
        integracaoStatus: "PENDENTE",
        dataVencimentoInicial: dataInicial,
        dataVencimentoFinal: dataFinal,
      }
      const response = await filterFinance("/graficos/faturamento-unidade", body)
      if (response?.data?.data) {
        setRevenueByUnitData(response.data.data)
      }
    } catch (err) {
      console.error("Erro ao buscar faturamento por unidade:", err)
    }
  }, [dataInicial, dataFinal])

  const processStudentsData = (students: StudentData[]) => {
    const activeCount = students.filter((student) => student.status === 1).length
    const totalCount = students.length
    const inactiveCount = totalCount - activeCount
    setActiveStudentsData([
      { name: "Ativos", value: activeCount, color: statusColors.ativo },
      { name: "Outros", value: inactiveCount, color: "#e5e7eb" },
    ])

    const adimplenteCount = students.filter((student) => student.status === 1).length
    const inadimplenteCount = students.filter((student) => student.status === 4).length
    if (inadimplenteCount > 0 || adimplenteCount > 0) {
      setPaymentStatusData([
        { name: "Adimplentes", value: adimplenteCount, color: statusColors.adimplente },
        { name: "Inadimplentes", value: inadimplenteCount, color: statusColors.inadimplente },
      ])
    } else {
      setPaymentStatusData([{ name: "Sem dados", value: 1, color: "#e5e7eb" }])
    }

    const blockedCount = students.filter((student) => student.status === 3).length
    const cancelledCount = students.filter((student) => student.status === 5).length
    if (blockedCount > 0 || cancelledCount > 0) {
      setBlockCancelData([
        { name: "Bloqueados", value: blockedCount, color: statusColors.bloqueado },
        { name: "Cancelados", value: cancelledCount, color: statusColors.cancelado },
      ])
    } else {
      setBlockCancelData([{ name: "Nenhum", value: 1, color: "#e5e7eb" }])
    }
  }

  const fetchProspectsData = useCallback(async () => {
    try {
      const unidadeId = params.get("id") ?? getUnitId()
      const response = await filterBase("/clientes/filtro?page=0&size=1000", {
        idUnidade: [unidadeId],
        status: 1,
      })
      const prospectsData = response?.data?.data?.content ?? []
      setProspectsData(prospectsData)
      setProspectsCount(prospectsData.length)
    } catch (err) {
      console.error("Erro ao buscar dados de prospects:", err)
    }
  }, [params, getUnitId])

  const processarVendasPorMes = (vendas: VendaData[]): ChartData[] => {
    const vendasPorMes = vendas.reduce(
      (acc, venda) => {
        try {
          const dataVenda = parseISO(venda.inclusao)
          const mesAno = format(dataVenda, "yyyy-MM", { locale: ptBR })
          const mesNome = format(dataVenda, "MMM", { locale: ptBR })
          if (!acc[mesAno]) {
            acc[mesAno] = {
              month: mesNome,
              vendas: 0,
              total: 0,
              periodo: mesAno,
            }
          }
          acc[mesAno].vendas += 1
          acc[mesAno].total += venda.total || 0
        } catch (error) {
          console.warn("Erro ao processar data:", venda.inclusao, error)
        }
        return acc
      },
      {} as Record<string, ChartData>,
    )
    return Object.values(vendasPorMes)
      .sort((a, b) => a.periodo.localeCompare(b.periodo))
      .map((item) => ({
        ...item,
        month: item.month.charAt(0).toUpperCase() + item.month.slice(1),
      }))
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value)
  }

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
          <p className="font-semibold text-gray-800">{`${label}`}</p>
          <p className="text-green-600">{`Vendas: ${payload[0]?.value || 0}`}</p>
          <p className="text-orange-600">{`Total: ${formatCurrency(payload[1]?.value || 0)}`}</p>
        </div>
      )
    }
    return null
  }

  const CustomPieTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0]
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
          <p className="font-semibold text-gray-800">{data.name}</p>
          <p style={{ color: data.payload.color }}>{`Quantidade: ${data.value}`}</p>
        </div>
      )
    }
    return null
  }

  const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, value }: any) => {
    if (percent < 0.05 || value === 0) return null
    const RADIAN = Math.PI / 180
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5
    const x = cx + radius * Math.cos(-midAngle * RADIAN)
    const y = cy + radius * Math.sin(-midAngle * RADIAN)
    return (
      <text
        x={x}
        y={y}
        fill="white"
        textAnchor={x > cx ? "start" : "end"}
        dominantBaseline="central"
        fontSize={12}
        fontWeight="bold"
      >
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    )
  }

  useEffect(() => {
    if (dataInicial && dataFinal) {
      fetchVendasData()
      fetchStudentsData()
      fetchProspectsData()
      fetchRevenueData()
      fetchRevenueByUnitData()
    }
  }, [dataInicial, dataFinal])

  if (loading && studentsLoading) {
    return (
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {[...Array(4)].map((_, i) => (
          <Card key={i}>
            <CardHeader>
              <CardTitle className="text-base">Carregando...</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    )
  }

  return (
    <div className="space-y-4 w-full h-full">
      {/* Students Status Charts Row */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4 h-[320px]">
        {/* Chart 1: Active Students */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-base font-medium">Alunos Ativos</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {studentsLoading ? (
              <div className="flex items-center justify-center h-48">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-green-600"></div>
              </div>
            ) : (
              <div className="h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={activeStudentsData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={renderCustomizedLabel}
                      outerRadius={90}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {activeStudentsData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <ChartTooltip content={<CustomPieTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="mt-2 text-center">
                  <p className="text-2xl font-bold text-green-600">{activeStudentsData[0]?.value || 0}</p>
                  <p className="text-sm text-muted-foreground">alunos ativos</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Chart 2: Payment Status */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-base font-medium">Status de Pagamento</CardTitle>
            <UserCheck className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {studentsLoading ? (
              <div className="flex items-center justify-center h-48">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-green-600"></div>
              </div>
            ) : (
              <div className="h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={paymentStatusData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={renderCustomizedLabel}
                      outerRadius={90}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {paymentStatusData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <ChartTooltip content={<CustomPieTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="mt-2 grid grid-cols-2 gap-2 text-center text-xs">
                  <div>
                    <p className="font-semibold text-green-600">
                      {paymentStatusData.find((d) => d.name === "Adimplentes")?.value || 0}
                    </p>
                    <p className="text-muted-foreground">Adimplentes</p>
                  </div>
                  <div>
                    <p className="font-semibold text-red-600">
                      {paymentStatusData.find((d) => d.name === "Inadimplentes")?.value || 0}
                    </p>
                    <p className="text-muted-foreground">Inadimplentes</p>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Chart 3: Blocked vs Cancelled */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-base font-medium ">Encerrados</CardTitle>
            <UserX className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {studentsLoading ? (
              <div className="flex items-center justify-center h-48">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-green-600"></div>
              </div>
            ) : (
              <div className="h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={blockCancelData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={renderCustomizedLabel}
                      outerRadius={90}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {blockCancelData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <ChartTooltip content={<CustomPieTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="mt-2 grid grid-cols-2 gap-2 text-center text-xs">
                  <div>
                    <p className="font-semibold text-gray-600">
                      {blockCancelData.find((d) => d.name === "Bloqueados")?.value || 0}
                    </p>
                    <p className="text-muted-foreground">Bloqueados</p>
                  </div>
                  <div>
                    <p className="font-semibold text-purple-600">
                      {blockCancelData.find((d) => d.name === "Cancelados")?.value || 0}
                    </p>
                    <p className="text-muted-foreground">Cancelados</p>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Chart 4: Prospects */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-base font-medium">Prospects</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {studentsLoading ? (
              <div className="flex items-center justify-center h-48">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-green-600"></div>
              </div>
            ) : (
              <div className="h-48 flex flex-col items-center justify-center">
                <div className="text-center">
                  <p className="text-4xl font-bold text-blue-600 mb-2">{prospectsCount}</p>
                  <p className="text-sm text-muted-foreground mb-4">prospects ativos</p>
                </div>
                {prospectsData.length > 0 && (
                  <div className="w-full">
                    <div className="text-xs text-muted-foreground mb-2">Últimos cadastros:</div>
                    <div className="space-y-1 max-h-20 overflow-y-auto">
                      {prospectsData
                        .sort((a, b) => new Date(b.inclusao).getTime() - new Date(a.inclusao).getTime())
                        .slice(0, 3)
                        .map((prospect) => (
                          <div key={prospect.id} className="text-xs bg-gray-50 p-1 rounded">
                            <div className="font-medium truncate">{prospect.nome}</div>
                            <div className="text-muted-foreground">
                              {format(parseISO(prospect.inclusao), "dd/MM/yyyy", { locale: ptBR })}
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Sales Chart */}
      <Card className="shadow-sm hover:shadow-md transition-shadow">
        <CardHeader >
          <CardTitle className="text-base">Vendas ao longo do tempo</CardTitle>
          {!loading && salesData.length > 0 && (
            <p className="text-sm text-gray-600">
              Total de {salesData.reduce((acc, item) => acc + item.vendas, 0)} vendas — Faturamento:{" "}
              {formatCurrency(salesData.reduce((acc, item) => acc + item.total, 0))}
            </p>
          )}
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
              <span className="ml-2 text-gray-600">Carregando dados...</span>
            </div>
          ) : error ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <p className="text-red-600 mb-2">Erro ao carregar dados</p>
                <p className="text-gray-500 text-sm">{error}</p>
                <Button onClick={fetchVendasData} className="mt-3" variant="outline">
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Tentar novamente
                </Button>
              </div>
            </div>
          ) : !salesData.length ? (
            <div className="flex items-center justify-center h-64">
              <p className="text-gray-500">Nenhuma venda encontrada no período selecionado</p>
            </div>
          ) : (
            <ChartContainer config={chartConfig}>
              <LineChart
                accessibilityLayer
                data={salesData}
                margin={{
                  top: 20,
                  left: 12,
                  right: 12,
                  bottom: 12,
                }}
              >
                <CartesianGrid vertical={false} strokeDasharray="3 3" />
                <XAxis dataKey="month" tickLine={false} axisLine={false} tickMargin={8} tick={{ fontSize: 12 }} />
                <YAxis yAxisId="vendas" orientation="left" tickLine={false} axisLine={false} tick={{ fontSize: 12 }} />
                <YAxis
                  yAxisId="total"
                  orientation="right"
                  tickLine={false}
                  axisLine={false}
                  tick={{ fontSize: 12 }}
                  tickFormatter={(value) => `R$ ${(value / 1000).toFixed(0)}k`}
                />
                <ChartTooltip cursor={false} content={<CustomTooltip />} />
                <Line
                  yAxisId="vendas"
                  dataKey="vendas"
                  type="monotone"
                  stroke="#00A000"
                  strokeWidth={3}
                  dot={{
                    fill: "#00A000",
                    strokeWidth: 2,
                    r: 4,
                  }}
                  activeDot={{
                    r: 6,
                    stroke: "#00A000",
                    strokeWidth: 2,
                  }}
                >
                  <LabelList position="top" offset={12} className="fill-green-600" fontSize={11} fontWeight="bold" />
                </Line>
                <Line
                  yAxisId="total"
                  dataKey="total"
                  type="monotone"
                  stroke="#EE7101"
                  strokeWidth={2}
                  strokeDasharray="5 5"
                  dot={{
                    fill: "#EE7101",
                    strokeWidth: 2,
                    r: 3,
                  }}
                  activeDot={{
                    r: 5,
                    stroke: "#EE7101",
                    strokeWidth: 2,
                  }}
                />
              </LineChart>
            </ChartContainer>
          )}
        </CardContent>
      </Card>

      {/* New Revenue Charts */}
      <div className="grid gap-4 md:grid-cols-2">
        {/* Gráfico de Faturamento por Mês */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Faturamento por Mês</CardTitle>
          </CardHeader>
          <CardContent>
            {revenueData.length === 0 ? (
              <div className="text-center py-10 text-gray-500">Nenhum dado disponível</div>
            ) : (
              <ChartContainer config={chartConfig}>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={revenueData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="label" />
                    <YAxis tickFormatter={(value) => formatCurrency(value)} />
                    <ChartTooltip />
                    <Bar dataKey="valor" fill="#10B981" />
                  </BarChart>
                </ResponsiveContainer>
              </ChartContainer>
            )}
          </CardContent>
        </Card>

        {/* Gráfico de Faturamento por Unidade */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Faturamento por Unidade</CardTitle>
          </CardHeader>
          <CardContent>
            {revenueByUnitData.length === 0 ? (
              <div className="text-center py-10 text-gray-500">Nenhum dado disponível</div>
            ) : (
              <ChartContainer config={chartConfig}>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={revenueByUnitData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="label" />
                    <YAxis tickFormatter={(value) => formatCurrency(value)} />
                    <ChartTooltip />
                    <Bar dataKey="valor" fill="#3B82F6" />
                  </BarChart>
                </ResponsiveContainer>
              </ChartContainer>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
